import { callBackend } from "./callBackend";

/**
 * Normalizes media items from backend API to ensure uniform URL format
 */
export const normalizeMediaUrl = (item) => {
  if (!item) return "";
  let rawUrl = "";
  if (typeof item === "string") {
    rawUrl = item;
  } else if (typeof item === "object") {
    rawUrl =
      item.url ||
      item.mediaUrl ||
      item.imageUrl ||
      item.videoUrl ||
      item.downloadURL ||
      item.downloadUrl ||
      item.publicUrl ||
      item.src ||
      item.uri ||
      "";
  }

  if (!rawUrl || typeof rawUrl !== "string") return "";
  let clean = rawUrl.trim();

  // If URL is missing alt=media for Firebase storage, ensure alt=media is attached
  if (clean.includes("firebasestorage.googleapis.com") && !clean.includes("alt=media")) {
    clean += clean.includes("?") ? "&alt=media" : "?alt=media";
  }

  return clean;
};

/**
 * Fetch all website media (images, videos, cover photos grouped by room)
 *
 * @param {Object} options
 * @param {string} [options.roomId] - Room category (e.g. "standardAC", "all")
 * @param {string} [options.mediaType] - "image" | "video" | "all"
 * @param {boolean} [options.onlyCovers] - Fetch only primary room covers
 * @param {boolean} [options.isFeatured] - Filter by featured status
 * @param {boolean} [options.isVisible] - Filter visible items (default: true)
 * @param {Array} [options.tags] - Filter by tags
 * @returns {Promise<{ coverPhotos: Object, coverList: Array, rooms: Object, images: Array, videos: Array, allMedia: Array, total: number }>}
 */
export const fetchWebsiteMedia = async (options = {}) => {
  try {
    const res = await callBackend("backendBookingService", "fetchWebsiteMedia", {
      isVisible: true,
      ...options,
    });
    if (res && res.success && res.data) {
      return res.data;
    }
    return res?.data || res || null;
  } catch (error) {
    console.warn("⚠️ fetchWebsiteMedia backend call failed:", error.message || error);
    return null;
  }
};

/**
 * Fetch cover photos specifically for all room types
 *
 * @param {Object} options
 * @returns {Promise<{ [roomKey: string]: Object }>}
 */
export const fetchCoverPhotos = async (options = {}) => {
  try {
    const res = await callBackend("backendBookingService", "fetchCoverPhotos", {
      isVisible: true,
      ...options,
    });
    if (res && res.success && res.data) {
      return res.data?.coverPhotos || res.data;
    }
    return res?.data?.coverPhotos || res?.data || {};
  } catch (error) {
    console.warn("⚠️ fetchCoverPhotos backend call failed:", error.message || error);
    return {};
  }
};

/**
 * Fetch media (images, videos, cover) for a specific room category
 *
 * @param {string} roomId - e.g. "standardAC", "standardNonAC", "doubleRoomAC", "doubleRoomNonAC"
 * @param {Object} options
 * @returns {Promise<{ images: string[], videos: string[], coverPhoto: string|null, cover: Object|null, mediaList: Object[] }>}
 */
export const fetchRoomMedia = async (roomId, options = {}) => {
  try {
    const res = await callBackend("backendBookingService", "fetchRoomMedia", {
      roomId,
      isVisible: true,
      ...options,
    });
    if (res && res.success && res.data) {
      return res.data;
    }
    return res?.data || null;
  } catch (error) {
    console.warn(`⚠️ fetchRoomMedia failed for room "${roomId}":`, error.message || error);
    return null;
  }
};
